import { apiClient } from './client';

// ============ Types ============

export enum UserRole {
    ADMIN = 'Admin',
    USER = 'User',
    SUPPORT = 'Support',
    AGENT = 'Agent',
    SUPER_AGENT = 'SuperAgent',
    CAISSIER = 'Caissier',
}

export interface User {
    id: string;
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    nom?: string;    // From client-service (nom de famille)
    prenom?: string; // From client-service (prénom)
    tel: string;
    role: UserRole;
    isActive: boolean;
    isFrozen: boolean;
    frozenReason?: string;
    frozenAt?: string;
    twoFactorEnabled: boolean;
    createdAt: string;
    lastLoginAt?: string;
}

export interface UserDetail extends User {
    devices: TrustedDevice[];
}

export interface TrustedDevice {
    id: string;
    deviceBrand: string;
    deviceModel: string;
    status: 'pending' | 'verified' | 'flagged';
    isBlocked: boolean;
    lastUsedAt: string;
}

export interface CreateUserDto {
    email: string;
    phone: string;
    firstName?: string;
    lastName?: string;
    role?: UserRole;
    username?: string;
    password?: string;
    pin?: string;
}

export interface UpdateUserDto {
    username?: string;
    email?: string;
    tel?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
}

export interface UserListResponse {
    users: User[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// ============ API Functions ============

export const usersApi = {
    // List users
    getUsers: async (page = 1, limit = 20, search = ''): Promise<UserListResponse> => {
        const response = await apiClient.get('/api/admin/users', {
            params: { page, limit, search }
        });
        return response.data;
    },

    // Get single user
    getUser: async (userId: string): Promise<UserDetail> => {
        const response = await apiClient.get(`/api/admin/users/${userId}`);
        return response.data;
    },

    // Create user
    createUser: async (data: CreateUserDto): Promise<User> => {
        const response = await apiClient.post('/api/admin/users', data);
        return response.data;
    },

    // Update user
    updateUser: async (userId: string, data: UpdateUserDto): Promise<User> => {
        const response = await apiClient.put(`/api/admin/users/${userId}`, data);
        return response.data;
    },

    // Freeze account
    freezeAccount: async (userId: string, reason: string): Promise<void> => {
        await apiClient.post(`/api/admin/users/${userId}/freeze`, { reason });
    },

    // Unfreeze account
    unfreezeAccount: async (userId: string): Promise<void> => {
        await apiClient.post(`/api/admin/users/${userId}/unfreeze`);
    },

    // Resend PIN Setup
    resendPinSetup: async (userId: string): Promise<void> => {
        await apiClient.post('/api/auth/users/resend-pin-setup', { userId, frontendUrl: window.location.origin });
    },

    // Get frozen accounts list
    getFrozenAccounts: async (): Promise<User[]> => {
        const response = await apiClient.get('/api/admin/users/frozen/list');
        return response.data;
    },

    // Block device
    blockDevice: async (deviceId: string, reason: string): Promise<void> => {
        await apiClient.post(`/api/admin/devices/${deviceId}/block`, { reason });
    },

    // Unblock device
    unblockDevice: async (deviceId: string): Promise<void> => {
        await apiClient.post(`/api/admin/devices/${deviceId}/unblock`);
    }
};
